import { AbstractMapDomain, MapDomainArrayManager, MapToolDefaults, MapToolState, MapObjectForm, TabDOMUtil, FilterAutocompleteFormInput, MapTool } from 'geovisto';

// Geovisto core
/**
 * This class wraps the equals filter operation.
 *
 * @author Jiri Hynek
 */
class EqFilterOperation extends AbstractMapDomain {
    /**
     * It creates the equals filter operation.
     */
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing the operator.
     */
    getName() {
        return "==";
    }
    /**
     * It checks if a value equals to a pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return value == pattern;
    }
}

// Geovisto core
/**
 * This class wraps the not-equals filter operation.
 *
 * @author Jiri Hynek
 */
class NeqFilterOperation extends AbstractMapDomain {
    /**
     * It creates the not-equals filter operation.
     */
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing the operator.
     */
    getName() {
        return "!=";
    }
    /**
     * It checks if value does not equal a pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return value != pattern;
    }
}

// Geovisto core
/**
 * This class wraps the regex filter operation.
 *
 * @author Jiri Hynek
 */
class RegFilterOperation extends AbstractMapDomain {
    /**
     * It creates the regular expression filter operation.
     */
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing the operator.
     */
    getName() {
        return "reg";
    }
    /**
     * It checks if a value match a regular expression pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return (new String(value)).match(new RegExp(pattern, 'g')) !== null;
    }
}

/**
 * This class wraps dataDomain, filterOperation, pattern which represents a filter rule.
 *
 * @author Jiri Hynek
 */
class MapFilterRule {
    /**
     * It creates a filter rule composed of given data domain, operation and pattern.
     *
     * @param dataDomain
     * @param operation
     * @param pattern
     */
    constructor(dataDomain, operation, pattern) {
        this.dataDomain = dataDomain;
        this.operation = operation;
        this.pattern = pattern;
    }
    /**
     * It returns the the data domain which should be analyzed.
     */
    getDataDomain() {
        return this.dataDomain;
    }
    /**
     * It returns the filter operation used for the filtering.
     */
    getFilterOperation() {
        return this.operation;
    }
    /**
     * It returns the string label of the filter representing operator given by the parameter of constructor.
     */
    getPattern() {
        return this.pattern;
    }
}

// Geovisto core
/**
 * This class provide functions for using filters.
 *
 * @author Jiri Hynek
 */
class MapFiltersManager extends MapDomainArrayManager {
    constructor(filterOperations) {
        super(filterOperations);
    }
    /**
     * The function creates a new filter rule using given operation label.
     * If operation does not exists it returns null.
     *
     * @param dataDomain
     * @param label
     * @param pattern
     */
    createRule(dataDomain, opName, pattern) {
        const operation = this.getDomain(opName);
        if (operation) {
            return new MapFilterRule(dataDomain, operation, pattern);
        }
        return null;
    }
    /**
     * Takes a list of data and applies the given filter rules.
     * Returns a new list of the references to filtered data items.
     *
     * TODO: define data records type
     *
     * @param dataManager
     * @param dataRecords
     * @param filterRules
     */
    filterData(dataManager, dataRecords, filterRules) {
        const resultData = [];
        // prepare arrays to save computation time
        const dataDomains = [];
        const matchFunctions = [];
        const patterns = [];
        for (let i = 0; i < filterRules.length; i++) {
            dataDomains.push(filterRules[i].getDataDomain());
            matchFunctions.push(filterRules[i].getFilterOperation().match);
            patterns.push(filterRules[i].getPattern());
        }
        // go through data
        let doFilter;
        let values;
        for (let i = 0; i < dataRecords.length; i++) {
            doFilter = false;
            for (let j = 0; j < filterRules.length; j++) {
                values = dataManager.getDataRecordValues(dataDomains[j], dataRecords[i]);
                if (values.length == 1) {
                    if (!matchFunctions[j](values[0], patterns[j])) {
                        doFilter = true;
                        break;
                    }
                }
            }
            if (!doFilter) {
                // data item is accepted by all filter rules
                resultData.push(dataRecords[i]);
            }
        }
        return resultData;
    }
}

// Geovisto core
/**
 * This class provide functions which return the default state values.
 *
 * @author Jiri Hynek
 */
class FiltersToolDefaults extends MapToolDefaults {
    /**
     * It returns default config if no config is given.
     */
    getConfig() {
        const config = super.getConfig();
        config.filterRules = undefined;
        return config;
    }
    /**
     * Only one filter tool should be present in the Geovisto map.
     */
    isSingleton() {
        return true;
    }
    /**
     * It returns a unique string of the tool type.
     */
    getType() {
        return FiltersToolDefaults.TYPE;
    }
    /**
     * It returns the label of the tool.
     */
    getLabel() {
        return "Filters";
    }
    /**
     * It returns the icon of the tool.
     */
    getIcon() {
        return '<i class="fa fa-filter"></i>';
    }
    /**
     * It returns default filters manager.
     */
    getFiltersManager() {
        return new MapFiltersManager([
            new EqFilterOperation(),
            new NeqFilterOperation(),
            new RegFilterOperation()
        ]);
    }
    /**
     * It returns default filter rules.
     */
    getFilterRules() {
        return [];
    }
}
/**
 * Static tool type constant.
 */
FiltersToolDefaults.TYPE = "geovisto-tool-filters";

// Geovisto core
/**
 * This class provide functions for using filters.
 *
 * @author Jiri Hynek
 */
class FiltersToolState extends MapToolState {
    /**
     * It creates a tool state.
     *
     * @param tool
     */
    constructor(tool) {
        super(tool);
    }
    /**
     * It resets state with respect to initial props.
     */
    initialize(defaults, props, initProps) {
        // set filter manager, which manages filter operations
        this.setFiltersManager(props.manager == undefined ? defaults.getFiltersManager() : props.manager);
        // set filter rules if specified in props explicitly
        this.setFilterRules(props.rules == undefined ? defaults.getFilterRules() : props.rules);
        // set super props
        super.initialize(defaults, props, initProps);
    }
    /**
     * The metod takes config and deserializes the values.
     *
     * @param config
     */
    deserialize(config) {
        var _a;
        super.deserialize(config);
        // deserialize filter rules
        if (config.filterRules) {
            // get filter and data manegers which are need for proper deserialization of filter rules
            const filterManager = this.getFiltersManager();
            const mapDataManager = (_a = this.getMap()) === null || _a === void 0 ? void 0 : _a.getState().getMapData();
            if (filterManager && mapDataManager) {
                const filterRules = [];
                let configFilterRule;
                let dataDomain;
                let filterRule;
                for (let i = 0; i < config.filterRules.length; i++) {
                    configFilterRule = config.filterRules[i];
                    // get data domain
                    dataDomain = mapDataManager.getDomain(configFilterRule.domain);
                    if (dataDomain && configFilterRule.operation && configFilterRule.pattern) {
                        filterRule = filterManager.createRule(dataDomain, configFilterRule.operation, configFilterRule.pattern);
                        if (filterRule) {
                            filterRules.push(filterRule);
                        }
                    }
                }
                this.setFilterRules(filterRules);
            }
        }
    }
    /**
     * The method serializes the tool state. Optionally, defaults can be set if property is undefined.
     *
     * @param defaults
     */
    serialize(defaults) {
        const config = super.serialize(defaults);
        // serialize filter filters
        if (this.rules != undefined) {
            config.filterRules = [];
            let filterRule;
            for (let i = 0; i < this.rules.length; i++) {
                filterRule = this.rules[i];
                config.filterRules.push({
                    domain: filterRule.getDataDomain().getName(),
                    operation: filterRule.getFilterOperation().toString(),
                    pattern: filterRule.getPattern()
                });
            }
        }
        return config;
    }
    /**
     * It returns filter manager
     */
    getFiltersManager() {
        return this.manager;
    }
    /**
     * It updates filter manager.
     *
     * @param manager
     */
    setFiltersManager(manager) {
        this.manager = manager;
    }
    /**
     * It returns the filterRules property of the tool state.
     */
    getFilterRules() {
        return this.rules;
    }
    /**
     * It sets the filterRules property of the tool state.
     *
     * @param filterRules
     */
    setFilterRules(filterRules) {
        this.rules = filterRules;
    }
}

// Geovisto core
/**
 * This class provides controls for management of filters map form inputs.
 *
 * @author Jiri Hynek
 */
class FiltersToolMapForm extends MapObjectForm {
    /**
     * It creates new map form with respect to the given props.
     *
     * @param tool
     */
    constructor(tool) {
        super(tool);
        this.btnGroup = null;
        this.inputs = [];
    }
    /**
     * A help function which returns data manager
     */
    getDataManager() {
        var _a;
        return (_a = this.getMapObject().getMap()) === null || _a === void 0 ? void 0 : _a.getState().getMapData();
    }
    /**
     * A help function which returns data manager
     */
    getFilterManager() {
        return this.getMapObject().getState().getFiltersManager();
    }
    /**
     * A help function which returns the element class.
     */
    getFilterRuleElementClass(objectType) {
        if (objectType) {
            return objectType + "-filter";
        }
        return "unknown-object-filter";
    }
    /**
     * It returns a HTML div element conatining the form.
     */
    getContent() {
        //if(this.htmlContent == undefined) {
        // tab pane
        this.htmlContent = document.createElement('div');
        // btn group
        this.btnGroup = this.htmlContent.appendChild(document.createElement('div'));
        this.btnGroup.setAttribute("class", "filterButtons");
        // append add button
        this.btnGroup.appendChild(TabDOMUtil.createButton("<i class=\"fa fa-plus-circle\"></i>", () => { this.addSelectItem(); }, "plusBtn"));
        // append apply button
        this.btnGroup.appendChild(TabDOMUtil.createButton("Apply", () => {
            this.inputChangedAction();
        }, "applyBtn"));
        // import inputs according to configuration
        this.setFilterRules(this.getMapObject().getState().getFilterRules());
        //}
        return this.htmlContent;
    }
    /**
     * Help static function which adds new select item to the filter map form.
     */
    addSelectItem() {
        var _a;
        // div container
        if (this.htmlContent) {
            const div = this.htmlContent.insertBefore(document.createElement('div'), this.btnGroup);
            div.classList.add(this.getFilterRuleElementClass(this.getMapObject().getType()));
            const minusButton = TabDOMUtil.createButton("<i class=\"fa fa-minus-circle\"></i>", (e) => { this.removeSelectItem(e); }, "minusBtn");
            div.appendChild(minusButton);
            /**
             * Help function which is invoked when the data domain input is changed.
             * It changes possible options of the operation and value inputs.
             *
             * TODO: specify type of the param
             *
             * @param e
             */
            const updateValueOptions = (e) => {
                var _a, _b, _c;
                // find the input item
                let input = null;
                const div = e.target.closest("." + this.getFilterRuleElementClass(this.getMapObject().getType()));
                for (let i = 0; i < this.inputs.length; i++) {
                    if (this.inputs[i].container == div) {
                        input = this.inputs[i].input;
                        break;
                    }
                }
                if (input) {
                    const inputElement = input.getInputElement();
                    if (inputElement) {
                        // get selected value of the data domain input
                        const dataDomainName = e.target.value;
                        // test if defined
                        if (dataDomainName != undefined && dataDomainName != "") {
                            // enable operation and value inputs
                            inputElement.op.setDisabled(false);
                            inputElement.val.setDisabled(false);
                            // find possible values of selected data domain
                            const dataDomain = (_a = this.getDataManager()) === null || _a === void 0 ? void 0 : _a.getDomain(dataDomainName);
                            if (dataDomain) {
                                // update options of the value input
                                // TODO specify the type
                                inputElement.val.setOptions((_c = (_b = this.getDataManager()) === null || _b === void 0 ? void 0 : _b.getValues(dataDomain)) !== null && _c !== void 0 ? _c : []);
                            }
                        }
                        else {
                            // disable operation and value inputs
                            inputElement.op.setDisabled(true);
                            inputElement.op.setValue("");
                            inputElement.val.setDisabled(true);
                            inputElement.val.setValue("");
                        }
                    }
                }
            };
            const dataManager = this.getDataManager();
            let dataDomainNames = dataManager === null || dataManager === void 0 ? void 0 : dataManager.getDomainNames();
            if (!dataDomainNames || dataDomainNames.length == 0) {
                dataDomainNames = [""];
            }
            const dataDomain = dataManager === null || dataManager === void 0 ? void 0 : dataManager.getDomain(dataDomainNames[0]);
            const operationNames = this.getFilterManager().getDomainNames();
            // inputs
            const input = new FilterAutocompleteFormInput({
                data: {
                    options: dataDomainNames,
                    onChangeAction: updateValueOptions
                },
                ops: {
                    options: operationNames,
                    onChangeAction: function () { }
                },
                vals: {
                    // TODO: specify the type
                    options: dataDomain ? (_a = dataManager === null || dataManager === void 0 ? void 0 : dataManager.getValues(dataDomain)) !== null && _a !== void 0 ? _a : [] : [],
                    onChangeAction: function () { }
                }
            });
            div.appendChild(input.create());
            // help input item
            const inputItem = {
                container: div,
                input: input
            };
            // list of input items
            this.inputs.push(inputItem);
            return inputItem;
        }
        return null;
    }
    /**
     * Help function which removes item from the filter map form.
     */
    removeSelectItem(e) {
        if (e.target) {
            // get div
            const div = e.target.closest("div");
            // find input item and remove it from DOM and list of input items
            for (let i = 0; i < this.inputs.length; i++) {
                if (this.inputs[i].container == div) {
                    div.remove();
                    this.inputs.splice(i, 1);
                    break;
                }
            }
        }
    }
    /**
     * It updates rules according to the input values.
     */
    inputChangedAction() {
        this.getMapObject().setFilterRules(this.getFilterRules());
    }
    /**
     * It updates input values according to the selection.
     */
    dimensionInputChangedAction() {
        // TODO
    }
    /**
     * It returns selected values from input fields and constructs filter rules.
     */
    getFilterRules() {
        var _a;
        const filterRules = [];
        let value;
        let dataDomain;
        for (let i = 0; i < this.inputs.length; i++) {
            // get input value
            value = this.inputs[i].input.getValue();
            // get data domain
            dataDomain = (_a = this.getDataManager()) === null || _a === void 0 ? void 0 : _a.getDomain(value.data);
            if (dataDomain) {
                // new filter rule
                const filterRule = this.getFilterManager().createRule(dataDomain, value.op, value.val);
                if (filterRule) {
                    filterRules.push(filterRule);
                }
            }
        }
        return filterRules;
    }
    /**
     * It updates input fileds according to the given filter rules.
     *
     * @param filterRules
     */
    setFilterRules(filterRules) {
        var _a;
        // clear inputs
        for (let i = 0; i < this.inputs.length; i++) {
            this.inputs[i].container.remove();
        }
        this.inputs = [];
        if (filterRules == undefined || filterRules.length == 0) {
            // if filter rules are empty, initialize one empty input item
            this.addSelectItem();
        }
        else {
            // import inputs according to given filter rules
            for (let i = 0; i < filterRules.length; i++) {
                // create input for given filter rule
                (_a = this.addSelectItem()) === null || _a === void 0 ? void 0 : _a.input.setValue({
                    data: filterRules[i].getDataDomain().getName().toString(),
                    op: filterRules[i].getFilterOperation().getName().toString(),
                    val: filterRules[i].getPattern()
                });
            }
        }
    }
}

// Geovisto core
/**
 * This class wraps filters, sidebar tab and state. It provides methods for filters management.
 *
 * @author Jiri Hynek
 */
class FiltersTool extends MapTool {
    /**
     * It creates a new tool with respect to the props.
     *
     * @param props
     */
    constructor(props) {
        super(props);
    }
    /**
     * It creates a copy of the uninitialized tool.
     */
    copy() {
        return new FiltersTool(this.getProps());
    }
    /**
     * It returns the props given by the programmer.
     */
    getProps() {
        return super.getProps();
    }
    /**
     * It returns default values of the filters tool.
     */
    getDefaults() {
        return super.getDefaults();
    }
    /**
     * It creates new defaults of the tool.
     */
    createDefaults() {
        return new FiltersToolDefaults();
    }
    /**
     * It returns the filters tool state.
     */
    getState() {
        return super.getState();
    }
    /**
     * It returns default tool state.
     */
    createState() {
        return new FiltersToolState(this);
    }
    /**
     * Overrides the super method.
     *
     * @param initProps
     */
    initialize(initProps) {
        return super.initialize(initProps);
    }
    /**
     * It creates new filter tool.
     */
    create() {
        // set filter rules
        this.setFilterRules(this.getState().getFilterRules());
        return this;
    }
    /**
     * It returns a map form.
     */
    getMapForm() {
        if (this.mapForm == undefined) {
            this.mapForm = this.createMapForm();
        }
        return this.mapForm;
    }
    /**
     * It creates new map form.
     */
    createMapForm() {
        return new FiltersToolMapForm(this);
    }
    /**
     * It updates filter rules and notifies listeners.
     *
     * @param filterRules
     */
    setFilterRules(filterRules) {
        if (filterRules != undefined) {
            const map = this.getMap();
            // if the filter tool is enabled, update map data
            if (this.isEnabled() && map) {
                const mapDataManager = map.getState().getMapData();
                map.updateCurrentData(this.getState().getFiltersManager().filterData(mapDataManager, mapDataManager.getDataRecords(), filterRules), this);
            }
            // update filter rules
            this.getState().setFilterRules(filterRules);
        }
    }
    /**
     * It changes filters state to enabled/disabled.
     *
     * @param enabled
     */
    setEnabled(enabled) {
        if (enabled != this.isEnabled()) {
            // update state
            this.getState().setEnabled(enabled);
            // the Geovisto map stores the current data and works as the event dispatcher
            const map = this.getMap();
            if (map) {
                // apply filter rules if enabled, else use empty list of filters (use the initial data)
                if (enabled) {
                    const mapData = map.getState().getMapData();
                    map.updateCurrentData(this.getState().getFiltersManager().filterData(mapData, mapData.getDataRecords(), this.getState().getFilterRules()), this);
                }
                else {
                    map.updateCurrentData(map.getState().getMapData().getDataRecords(), this);
                }
            }
            super.setEnabled(enabled);
        }
    }
}

const GeovistoFiltersTool = {
    getType: () => FiltersToolDefaults.TYPE,
    createTool: (props) => new FiltersTool(props),
    createFiltersManager: (filterOperations) => new MapFiltersManager(filterOperations),
    createFilterOperationEq: () => new EqFilterOperation(),
    createFilterOperationNeq: () => new NeqFilterOperation(),
    createFilterOperationReg: () => new RegFilterOperation()
};

// Geovisto core
/**
 * This class wraps a filter operation defined by constructor props.
 *
 * @author Jiri Hynek
 */
class MapFilterOperation extends AbstractMapDomain {
    /**
     * It creates a new filter operation.
     *
     * @param label
     * @param acceptFunction
     */
    constructor(label, acceptFunction) {
        super();
        this.label = label;
        this.match = acceptFunction;
    }
    /**
     * It returns a unique string label of the filter representing operator given by the parameter of constructor.
     */
    getName() {
        return this.label;
    }
}

// Geovisto core
/**
 * This class wraps the greater than equals filter operation.
 *
 * @author Jiri Hynek
 */
class GtEqFilterOperation extends AbstractMapDomain {
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing operator.
     */
    getName() {
        return ">=";
    }
    /**
     * It checks if value equals pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return new String(value).toString() >= pattern;
    }
}

// Geovisto core
/**
 * This class wraps the equals greater than filter operation.
 *
 * @author Jiri Hynek
 */
class GtFilterOperation extends AbstractMapDomain {
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing operator.
     */
    getName() {
        return ">";
    }
    /**
     * It checks if value equals pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return new String(value).toString() > pattern;
    }
}

// Geovisto core
/**
 * This class wraps the less than equals filter operation.
 *
 * @author Jiri Hynek
 */
class LtEqFilterOperation extends AbstractMapDomain {
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing operator.
     */
    getName() {
        return "<=";
    }
    /**
     * It checks if value equals pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return new String(value).toString() <= pattern;
    }
}

// Geovisto core
/**
 * This class wraps the less than filter operation.
 *
 * @author Jiri Hynek
 */
class LtFilterOperation extends AbstractMapDomain {
    constructor() {
        super();
    }
    /**
     * It returns the string label of the filter representing operator.
     */
    getName() {
        return "<";
    }
    /**
     * It checks if value equals pattern.
     *
     * @param value
     * @param pattern
     */
    match(value, pattern) {
        return new String(value).toString() < pattern;
    }
}

export { EqFilterOperation, FiltersTool, FiltersToolDefaults, FiltersToolMapForm, FiltersToolState, GeovistoFiltersTool, GtEqFilterOperation, GtFilterOperation, LtEqFilterOperation, LtFilterOperation, MapFilterOperation, MapFilterRule, MapFiltersManager, NeqFilterOperation, RegFilterOperation };
//# sourceMappingURL=index.es.js.map
